import { Link, useLocation } from "react-router-dom";

import { browserShareDevice, shareLink } from "@/lib/shareLink";
import { useFoldAway } from "./timeline/useFoldAway";

export interface Crumb {
  to: string;
  label: string;
  /** What the phone shows when the full label is too long for its top bar. */
  shortLabel?: string;
}

/** A phone has room for where you are and one step back. The rest waits in the menu. */
export function AppNavPhone({ crumbs }: { crumbs: readonly Crumb[] }) {
  const { rootRef, unfolded, toggle } = useFoldAway();
  const current = crumbs.at(-1);
  const { pathname } = useLocation();
  // In the player the book is the last crumb but not this page, so back goes to the book.
  const parent = current !== undefined && current.to !== pathname ? current : crumbs.at(-2);
  return (
    <div className="app-nav__phone">
      {parent === undefined ? null : (
        <Link className="app-nav__back" to={parent.to} aria-label={`Back to ${parent.label}`}>
          ‹
        </Link>
      )}
      <span className="app-nav__current">{current?.shortLabel ?? current?.label}</span>
      <div ref={rootRef} className="app-nav__menu">
        <button
          type="button"
          className="app-nav__menu-button"
          aria-label="Menu"
          aria-expanded={unfolded}
          onClick={toggle}
        >
          ☰
        </button>
        {unfolded ? (
          <nav className="app-nav__menu-list" aria-label="Menu">
            {crumbs.map((crumb) => (
              <Link
                key={crumb.to}
                to={crumb.to}
                aria-current={crumb.to === pathname ? "page" : undefined}
                onClick={toggle}
              >
                {crumb.label}
              </Link>
            ))}
            {current?.to === "/legal" ? null : <Link to="/legal" onClick={toggle}>Legal</Link>}
            <button
              type="button"
              className="app-nav__menu-share"
              onClick={() => {
                toggle();
                void shareLink(window.location.href, document.title, browserShareDevice())
                  .catch(() => window.prompt("Copy this link", window.location.href));
              }}
            >
              Share this page
            </button>
          </nav>
        ) : null}
      </div>
    </div>
  );
}
